import React, { useEffect } from 'react';
import { Form, Input, Button, Card, Tabs, message } from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../../api/axios';
import { SaveOutlined } from '@ant-design/icons';

export const OleContactInfoSettings: React.FC = () => {
    const [form] = Form.useForm();
    const queryClient = useQueryClient();

    const { data: contactData, isLoading } = useQuery({
        queryKey: ['adminOleContactInfo'],
        queryFn: async () => {
            const res = await apiClient.get('/admin/ole/contact-info');
            return res.data;
        }
    });

    useEffect(() => {
        if (contactData && contactData.succeeded && contactData.data) {
            const cd = contactData.data;
            
            const formattedTranslations: any = {};
            cd.translations?.forEach((t: any) => {
                formattedTranslations[t.languageCode] = {
                    openingHours: t.openingHours,
                    organizationName: t.organizationName,
                    locationNote: t.locationNote
                };
            });

            form.setFieldsValue({
                email: cd.email,
                emailOrganization: cd.emailOrganization,
                phone: cd.phone,
                whatsApp: cd.whatsApp,
                address: cd.address,
                city: cd.city,
                postalCode: cd.postalCode,
                registryNumber: cd.registryNumber,
                representativeName: cd.representativeName,
                translations: formattedTranslations
            });
        }
    }, [contactData, form]);

    const mutation = useMutation({
        mutationFn: async (values: any) => {
            const translations = Object.keys(values.translations || {}).map(lang => ({
                languageCode: lang,
                openingHours: values.translations[lang]?.openingHours || '',
                organizationName: values.translations[lang]?.organizationName || '',
                locationNote: values.translations[lang]?.locationNote || ''
            }));

            const payload = {
                email: values.email || '',
                emailOrganization: values.emailOrganization || '',
                phone: values.phone || '',
                whatsApp: values.whatsApp || '',
                address: values.address || '',
                city: values.city || '',
                postalCode: values.postalCode || '',
                registryNumber: values.registryNumber || '',
                representativeName: values.representativeName || '',
                translations
            };

            return apiClient.put('/admin/ole/contact-info', payload);
        },
        onSuccess: () => {
            message.success('İletişim bilgileri başarıyla güncellendi');
            queryClient.invalidateQueries({ queryKey: ['adminOleContactInfo'] });
        },
        onError: () => {
            message.error('İletişim bilgileri güncellenirken hata oluştu');
        }
    });

    const tabItems = [
        { key: 'de', label: 'Almanca (DE)' },
        { key: 'en', label: 'İngilizce (EN)' },
        { key: 'tr', label: 'Türkçe (TR)' }
    ];

    if (isLoading) return <div>Yükleniyor...</div>;

    return (
        <Card title="OLE İletişim Bilgileri">
            <Form form={form} layout="vertical" onFinish={(values) => mutation.mutate(values)}>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                    <Form.Item name="email" label="Genel Email">
                        <Input />
                    </Form.Item>
                    <Form.Item name="emailOrganization" label="Kurumsal Email">
                        <Input />
                    </Form.Item>
                    <Form.Item name="phone" label="Telefon">
                        <Input />
                    </Form.Item>
                    <Form.Item name="whatsApp" label="WhatsApp">
                        <Input />
                    </Form.Item>
                    <Form.Item name="address" label="Adres">
                        <Input />
                    </Form.Item>
                    <Form.Item name="city" label="Şehir">
                        <Input />
                    </Form.Item>
                    <Form.Item name="postalCode" label="Posta Kodu">
                        <Input />
                    </Form.Item>
                    <Form.Item name="registryNumber" label="Kayıt Numarası (VR vb.)">
                        <Input />
                    </Form.Item>
                    <Form.Item name="representativeName" label="Temsilci / Başkan">
                        <Input />
                    </Form.Item>
                </div>

                <Tabs defaultActiveKey="de" items={tabItems.map(tab => ({
                    key: tab.key,
                    label: tab.label,
                    children: (
                        <div style={{ padding: '1rem 0' }}>
                            <Form.Item name={['translations', tab.key, 'organizationName']} label="Kurum Adı">
                                <Input />
                            </Form.Item>
                            <Form.Item name={['translations', tab.key, 'openingHours']} label="Çalışma Saatleri">
                                <Input.TextArea rows={3} />
                            </Form.Item>
                            <Form.Item name={['translations', tab.key, 'locationNote']} label="Konum Notu (Yol tarifi)">
                                <Input.TextArea rows={2} />
                            </Form.Item>
                        </div>
                    )
                }))} />

                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={mutation.isPending} icon={<SaveOutlined />}>
                        Güncelle
                    </Button>
                </Form.Item>
            </Form>
        </Card>
    );
};
